import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bell,
  CheckCheck,
  Clock,
  FileCheck2,
  MapPin,
  Megaphone,
  MessageSquareWarning,
  Settings as SettingsIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

import { AnnouncementComposer } from "@/components/AnnouncementComposer";
import {
  notificationsApi,
  type NotificationItem,
  type NotificationSeverity,
  type NotificationType,
} from "@/lib/notifications-api";
import { ApiError } from "@/lib/api";
import { useInvalidateUnreadCount } from "@/hooks/useUnreadNotifications";
import { formatDateIn } from "@/lib/datetime";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";

const TYPE_FILTERS: Array<{ value: "all" | NotificationType; label: string }> = [
  { value: "all", label: "All" },
  { value: "OUTSIDE_GEOFENCE", label: "Geofence" },
  { value: "MISSED_ATTENDANCE", label: "Missed" },
  { value: "MISSED_CHECK_OUT", label: "Still in" },
  { value: "PENDING_REQUEST", label: "Requests" },
  { value: "ANNOUNCEMENT", label: "Announcements" },
  { value: "ANOMALY", label: "Anomalies" },
  { value: "SYSTEM", label: "System" },
];

function iconFor(t: NotificationType) {
  switch (t) {
    case "OUTSIDE_GEOFENCE":
      return MapPin;
    case "MISSED_ATTENDANCE":
      return AlertTriangle;
    case "MISSED_CHECK_OUT":
      return Clock;
    case "PENDING_REQUEST":
      return FileCheck2;
    case "APPROVAL":
    case "REQUEST_APPROVED":
      return CheckCheck;
    case "REQUEST_REJECTED":
    case "REQUEST_NEEDS_CLARIFICATION":
      return MessageSquareWarning;
    case "ANNOUNCEMENT":
      return Megaphone;
    case "ANOMALY":
      return Activity;
    case "CHECK_IN_REMINDER":
      return Bell;
    default:
      return SettingsIcon;
  }
}

function severityStyle(s: NotificationSeverity): string {
  if (s === "CRITICAL") return "bg-destructive/10 text-destructive border-destructive/20";
  if (s === "WARNING") return "bg-warning/10 text-warning border-warning/20";
  return "bg-info/10 text-info border-info/20";
}

function relativeTime(iso: string, tz: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDateIn(d, tz);
}

const Notifications = () => {
  const [list, setList] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | NotificationType>("all");
  const [composerOpen, setComposerOpen] = useState(false);
  const navigate = useNavigate();
  const invalidateUnreadCount = useInvalidateUnreadCount();

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await notificationsApi.list({ page: 1, pageSize: 100 });
      setList(res.data);
      // A fresh list also means a fresh unread count — keep the header in sync
      // so the badge matches what the user is about to scan through.
      invalidateUnreadCount();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const visible = useMemo(
    () => (filter === "all" ? list : list.filter((n) => n.type === filter)),
    [list, filter],
  );

  // Split into Today / Earlier by calendar date so admins see timeline at a glance.
  const { today, earlier } = useMemo(() => {
    const t: NotificationItem[] = [];
    const e: NotificationItem[] = [];
    const todayStr = new Date().toDateString();
    for (const n of visible) {
      if (new Date(n.createdAt).toDateString() === todayStr) t.push(n);
      else e.push(n);
    }
    return { today: t, earlier: e };
  }, [visible]);

  const markRead = async (id: string) => {
    // Optimistic update — revert on failure.
    const prev = list;
    setList((p) => p.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    try {
      await notificationsApi.markRead(id);
      invalidateUnreadCount();
    } catch (e) {
      setList(prev);
      toast.error(e instanceof ApiError ? e.message : "Failed to mark read");
    }
  };

  const markAll = async () => {
    const prev = list;
    setList((p) => p.map((n) => ({ ...n, isRead: true })));
    try {
      await notificationsApi.markAllRead();
      invalidateUnreadCount();
      toast.success("All notifications marked as read");
    } catch (e) {
      setList(prev);
      toast.error(e instanceof ApiError ? e.message : "Failed to mark all read");
    }
  };

  return (
    <div className="space-y-5">
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
            <TabsList>
              {TYPE_FILTERS.map((f) => (
                <TabsTrigger key={f.value} value={f.value}>
                  {f.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" onClick={() => setComposerOpen(true)}>
              <Megaphone className="mr-1.5 h-3.5 w-3.5" />
              Send announcement
            </Button>
            <Button size="sm" variant="outline" onClick={markAll}>
              <CheckCheck className="mr-1.5 h-3.5 w-3.5" />
              Mark all as read
            </Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card className="p-12 text-center text-muted-foreground">Loading…</Card>
      ) : (
        <>
          <Section title="Today" items={today} onMarkRead={markRead} onNavigate={navigate} />
          <Section title="Earlier" items={earlier} onMarkRead={markRead} onNavigate={navigate} />

          {visible.length === 0 && (
            <Card className="p-12 text-center text-muted-foreground">
              <Bell className="mx-auto mb-2 h-8 w-8 opacity-40" />
              You're all caught up.
            </Card>
          )}
        </>
      )}

      <AnnouncementComposer
        open={composerOpen}
        onOpenChange={setComposerOpen}
        onSent={() => {
          setComposerOpen(false);
          loadList();
        }}
      />
    </div>
  );
};

function Section({
  title,
  items,
  onMarkRead,
  onNavigate,
}: {
  title: string;
  items: NotificationItem[];
  onMarkRead: (id: string) => void;
  onNavigate: (to: string) => void;
}) {
  const tz = useTenantTimezone();
  if (items.length === 0) return null;
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">
        {title}
      </div>
      <Card className="divide-y">
        {items.map((n) => {
          const Icon = iconFor(n.type);
          // `actionLink` may be a web path (/requests/:id) — mobile deeplinks
          // (app://...) are filtered out since the admin UI can't navigate there.
          const webLink = n.actionLink && !n.actionLink.startsWith("app://") ? n.actionLink : null;
          return (
            <div
              key={n.id}
              className={`flex gap-3 p-4 transition-colors hover:bg-secondary/30 ${
                !n.isRead ? "bg-primary/[0.02]" : ""
              }`}
            >
              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${severityStyle(
                  n.severity,
                )}`}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {!n.isRead && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                  <span className="text-sm font-medium">{n.title}</span>
                  <Badge variant="outline" className="text-[10px] capitalize">
                    {n.severity.toLowerCase()}
                  </Badge>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {relativeTime(n.createdAt, tz)}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">{n.body}</p>
                <div className="mt-2 flex items-center gap-2">
                  {webLink && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => {
                        onMarkRead(n.id);
                        onNavigate(webLink);
                      }}
                    >
                      Open <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  )}
                  {!n.isRead && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => onMarkRead(n.id)}
                    >
                      Mark as read
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

export default Notifications;
